import React, { useState } from "react";
import Reveal from "@/components/Reveal";
import { Phone, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Uzupełnij imię, e-mail i wiadomość.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contacts", { ...form, kind: "contact" });
      toast.success("Wiadomość wysłana. Odezwiemy się wkrótce.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative py-24 md:py-36 bg-[#080808]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left — info + map */}
          <Reveal className="lg:col-span-5 space-y-10">
            <div>
              <p className="overline mb-6">Kontakt</p>
              <h2 className="section-title text-white text-4xl md:text-6xl leading-[1.04]">
                Porozmawiajmy
                <br />
                <span className="italic text-neutral-300">o Twoim projekcie</span>.
              </h2>
            </div>

            <ul className="space-y-6">
              <li className="flex gap-4">
                <Phone size={18} strokeWidth={1} className="text-[var(--accent)] mt-1 shrink-0" />
                <div>
                  <p className="overline text-neutral-400 mb-1">Telefon</p>
                  <a
                    data-testid="contact-phone-1"
                    href="tel:+48607562129"
                    className="block text-white text-lg hover:text-[var(--accent)] transition-colors"
                  >
                    +48 607 562 129
                  </a>
                  <a
                    data-testid="contact-phone-2"
                    href="tel:+48726747752"
                    className="block text-white text-lg hover:text-[var(--accent)] transition-colors"
                  >
                    +48 726 747 752
                  </a>
                </div>
              </li>

              <li className="flex gap-4">
                <Mail size={18} strokeWidth={1} className="text-[var(--accent)] mt-1 shrink-0" />
                <div>
                  <p className="overline text-neutral-400 mb-1">E-mail</p>
                  <a
                    data-testid="contact-email"
                    href="mailto:kamieniarstwopaczkow@gmail.com"
                    className="text-white text-lg hover:text-[var(--accent)] transition-colors break-all"
                  >
                    kamieniarstwopaczkow@gmail.com
                  </a>
                </div>
              </li>

              <li className="flex gap-4">
                <MapPin size={18} strokeWidth={1} className="text-[var(--accent)] mt-1 shrink-0" />
                <div>
                  <p className="overline text-neutral-400 mb-1">Adres zakładu</p>
                  <p className="text-white">Chałupki 1, 57-220 Ziębice</p>
                  <p className="text-neutral-400 text-sm mt-2">
                    Druga lokalizacja: <span className="text-white">Paczków, ul. Dworcowa 10</span>
                  </p>
                </div>
              </li>

              <li className="flex gap-4">
                <Clock size={18} strokeWidth={1} className="text-[var(--accent)] mt-1 shrink-0" />
                <div>
                  <p className="overline text-neutral-400 mb-1">Godziny otwarcia</p>
                  <p className="text-white">poniedziałek – piątek</p>
                  <p className="text-neutral-300">7:00 – 16:00</p>
                </div>
              </li>
            </ul>

            <div className="hairline aspect-[4/3] overflow-hidden">
              <iframe
                title="Mapa dojazdu — Paczków, ul. Dworcowa 10"
                src="https://www.google.com/maps?q=Paczk%C3%B3w+ul.+Dworcowa+10&output=embed"
                className="w-full h-full grayscale contrast-125"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>

          {/* Right — form */}
          <Reveal delay={150} className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="hairline bg-[#0a0a0a] p-8 md:p-12 space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Imię i nazwisko *">
                  <input
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    type="text"
                    required
                    className={inputCls}
                  />
                </Field>
                <Field label="E-mail *">
                  <input
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={update("email")}
                    type="email"
                    required
                    className={inputCls}
                  />
                </Field>
                <Field label="Telefon">
                  <input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    type="tel"
                    className={inputCls}
                  />
                </Field>
                <Field label="Temat">
                  <input
                    data-testid="contact-input-subject"
                    value={form.subject}
                    onChange={update("subject")}
                    type="text"
                    placeholder="np. Wycena nagrobka"
                    className={inputCls}
                  />
                </Field>
              </div>

              <Field label="Wiadomość *">
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={6}
                  required
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 max-w-md">
                  Wysyłając formularz wyrażasz zgodę na kontakt w sprawie zapytania.
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-50"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                  {loading ? "Wysyłanie..." : "Wyślij wiadomość"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-transparent border border-white/15 text-white px-4 py-3.5 text-sm focus:outline-none focus:border-white transition-colors placeholder:text-neutral-600";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block overline text-neutral-400 mb-2">{label}</span>
    {children}
  </label>
);
